/**
 * Assets API
 * Handles all asset-related API calls
 */
import apiClient from './apiClient';
import {
  Asset,
  AssetMinimal,
  AssetsListResponse,
  AssetSummary,
  AssetAssignment,
  AssetHistoryResponse,
  CreateAssetData,
  UpdateAssetData,
  AssignAssetData,
  ReturnAssetData,
} from '../types';

export interface AssetsQueryParams {
  category_id?: number;
  state_id?: number;
  location_id?: number;
  assigned_to?: number;
  search?: string;
  include_inactive?: boolean;
}

export const assetsApi = {
  /**
   * Get all assets with optional filters
   */
  getAssets: async (params?: AssetsQueryParams): Promise<AssetsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.category_id) queryParams.append('category_id', params.category_id.toString());
    if (params?.state_id) queryParams.append('state_id', params.state_id.toString());
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.assigned_to) queryParams.append('assigned_to', params.assigned_to.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.include_inactive) queryParams.append('include_inactive', 'true');

    const query = queryParams.toString();
    return apiClient.get<AssetsListResponse>(`/assets${query ? `?${query}` : ''}`);
  },

  /**
   * Get asset summary statistics
   */
  getSummary: async (): Promise<AssetSummary> => {
    return apiClient.get<AssetSummary>('/assets/summary');
  },

  /**
   * Get a specific asset by ID
   */
  getAsset: async (assetId: number): Promise<{ asset: Asset }> => {
    return apiClient.get<{ asset: Asset }>(`/assets/${assetId}`);
  },

  /**
   * Get asset password (separate endpoint for security)
   */
  getAssetPassword: async (assetId: number): Promise<{ asset_id: string; password: string | null }> => {
    return apiClient.get<{ asset_id: string; password: string | null }>(`/assets/${assetId}/password`);
  },

  /**
   * Create a new asset
   */
  createAsset: async (data: CreateAssetData): Promise<{ message: string; asset: Asset }> => {
    return apiClient.post<{ message: string; asset: Asset }>('/assets', data);
  },

  /**
   * Update an existing asset
   */
  updateAsset: async (
    assetId: number,
    data: UpdateAssetData
  ): Promise<{ message: string; asset: Asset }> => {
    return apiClient.put<{ message: string; asset: Asset }>(`/assets/${assetId}`, data);
  },

  /**
   * Delete (soft delete) an asset
   */
  deleteAsset: async (assetId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/assets/${assetId}`);
  },

  /**
   * Assign an asset to an employee
   */
  assignAsset: async (
    assetId: number,
    data: AssignAssetData
  ): Promise<{ message: string; asset: Asset; assignment: AssetAssignment }> => {
    return apiClient.post<{ message: string; asset: Asset; assignment: AssetAssignment }>(
      `/assets/${assetId}/assign`,
      data
    );
  },

  /**
   * Process asset return
   */
  returnAsset: async (
    assetId: number,
    data?: ReturnAssetData
  ): Promise<{ message: string; asset: Asset }> => {
    return apiClient.post<{ message: string; asset: Asset }>(`/assets/${assetId}/return`, data || {});
  },

  /**
   * Get assignment history for an asset
   */
  getAssetHistory: async (assetId: number): Promise<AssetHistoryResponse> => {
    return apiClient.get<AssetHistoryResponse>(`/assets/${assetId}/history`);
  },

  /**
   * Get all current asset assignments
   */
  getCurrentAssignments: async (params?: {
    category_id?: number;
    location_id?: number;
    employee_id?: number;
    search?: string;
  }): Promise<{ assignments: CurrentAssignment[]; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.category_id) queryParams.append('category_id', params.category_id.toString());
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.employee_id) queryParams.append('employee_id', params.employee_id.toString());
    if (params?.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return apiClient.get<{ assignments: CurrentAssignment[]; total: number }>(
      `/assets/assignments${query ? `?${query}` : ''}`
    );
  },

  /**
   * Get users that can be assigned assets (excludes Super Admin)
   */
  getAssignableUsers: async (): Promise<{ users: AssignableUser[] }> => {
    return apiClient.get<{ users: AssignableUser[] }>('/assets/assignable-users');
  },
};

// Type for current assignment list view
export interface CurrentAssignment {
  id: number;
  asset_id: string;
  asset_db_id: number;
  category: string | null;
  model: string | null;
  serial_number: string | null;
  state: string | null;
  state_color: string | null;
  location: string | null;
  employee_id: number;
  employee_name: string;
  employee_email: string;
  employee_department: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  expected_return: string | null;
  notes: string | null;
}

// Type for assignable user
export interface AssignableUser {
  id: number;
  name: string;
  email: string;
  department: string | null;
}

export default assetsApi;
